import { Avatar, Container, Typography, useTheme } from '@mui/material';
import React, { useState } from 'react';

import { ChatWindowProps, MessageType } from './ChatWindowTypes';
import { getLocalDateAndTime } from './chatWindowUtils';

interface ChatWindowMessageProps {
  message: ChatWindowProps['messages'][0];
  index: number;
  userInitials: string | undefined;
}

export const ChatWindowMessage = ({ message, index, userInitials }: ChatWindowMessageProps) => {
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const isUserRA = message.currentUser === 'research_assistant';
  const isConceptGroupReview = message.type === MessageType.CONCEPT_GROUP_REVIEW;
  const isMappingResults = message.type === MessageType.OMOP_MAPPING_RESULTS;
  const isAnalyticalVariable = message.type === MessageType.ANALYTICAL_VARIABLE;
  const isProgress = message.type === MessageType.PROGRESS;
  const isInfoMessage =
    Boolean(message.type) &&
    !isConceptGroupReview &&
    !isMappingResults &&
    !isAnalyticalVariable &&
    !isProgress;
  const shouldHideRAAvatar = isUserRA;
  // Avatar initials prefer the message-level value (already sanitized by the container),
  // then fall back to the viewer's initials, and finally the first letter of currentUser.
  const userAvatar =
    message.userInitials ?? userInitials ?? message.currentUser.charAt(0).toUpperCase();

  // Info messages (system messages) are centered and have different styling
  if (isInfoMessage) {
    const isStringInfoMessage = typeof message.message === 'string';
    return (
      <Container
        key={index}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          '&.MuiContainer-root': {
            padding: '0',
            margin: `${theme.spacing(0)} 0 0`,
          },
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography
          sx={{
            margin: theme.spacing(2),
            padding: theme.spacing(1),
          }}
          component={isStringInfoMessage ? 'p' : 'div'}
          variant="caption"
        >
          {isStringInfoMessage
            ? (message.message as string).split('\n').map((line: string, lineIndex: number) => (
                <span key={lineIndex}>
                  {line}
                  {message.timestamp && <span> -- {getLocalDateAndTime(message.timestamp)}</span>}
                  <br />
                </span>
              ))
            : message.message}
        </Typography>
      </Container>
    );
  }

  // Regular messages (user and research assistant)
  const messageRole = isUserRA ? 'assistant' : 'user';
  return (
    <Container
      key={index}
      data-message-role={messageRole}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        display: 'flex',
        gap: theme.spacing(2),
        flexDirection: 'row',
        width: '100%',
        '&.MuiContainer-root': {
          padding: `0 ${theme.spacing(3)}`,
          margin: `${theme.spacing(5)} 0 0`,
        },
      }}
    >
      {/* Only show avatar for user messages, not for research_assistant */}
      {!shouldHideRAAvatar && (
        <Avatar
          alt={userAvatar}
          sx={{
            width: theme.spacing(6),
            height: theme.spacing(6),
            fontSize: theme.spacing(3),
            fontWeight: '700',
            backgroundColor: theme.palette.secondary.main,
          }}
        >
          {userAvatar}
        </Avatar>
      )}
      <Container
        sx={{
          flex: 1,
          minWidth: 0,
          '&.MuiContainer-root': {
            padding: '0',
          },
        }}
      >
        {message.message && (
          <Typography
            sx={{
              paddingInline: theme.spacing(3),
              paddingBlock: isUserRA ? 0 : theme.spacing(2),
              color: theme.palette.text.primary,
              paddingTop: isUserRA ? 0 : undefined,
              borderRadius: '.8rem',
              // TODO: no VDS token for this value — needs designer input (#eee message background)
              backgroundColor: !isUserRA ? '#eee' : undefined,
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
            }}
            component={typeof message.message === 'string' ? 'p' : 'div'}
            variant="body2"
          >
            {typeof message.message === 'string'
              ? message.message.split('\n').map((line, lineIndex) => (
                  <span key={lineIndex}>
                    {line}
                    <br />
                  </span>
                ))
              : message.message}
          </Typography>
        )}
        {/* Action - pass isHovered to child components that support it */}
        {message.action &&
          (React.isValidElement(message.action)
            ? React.cloneElement(
                message.action as React.ReactElement<{ isMessageHovered?: boolean }>,
                { isMessageHovered: isHovered }
              )
            : message.action)}
      </Container>
    </Container>
  );
};
